import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { actions } from './../store';

import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';
import { getDescription as getModifyRelatedEntitiesDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ModifyRelatedEntitiesCard';
import { getDescription as getScheduleActivityCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ScheduleActivityCard';
import { getDescription as getDeleteScheduledActivityCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/DeleteScheduledActivityCard';
import { getDescription as getModifyAutomationsCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ModifyAutomationsCard';
import { getDescription as getArchiveRecordCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ArchiveRecordCard';
import { getDescription as getSearchRecordsCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/SearchRecordsCard';
import { getDescription as getHttpRequestCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/HttpRequestCard';
import { getDescription as getChangeFieldValueCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ChangeFieldValueCard';
import { getDescription as getChangeTagsCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ChangeTagsCard';
import { getDescription as getMathOperatorCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/MathOperatorCard';
import { getDescription as getSendEmailCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/SendEmailCard';
import { getDescription as getSendTextCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/SendTextCard';
import { getDescription as getSendRelatedContactTextCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/SendRelatedContactTextCard';
import { getDescription as getCreateRelatedEntityCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/CreateRelatedEntityCard';
import { getDescription as getStartAutomationCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/StartAutomationCard';
import { getDescription as getSendRelatedContactEmailCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/SendRelatedContactEmailCard';
import { getDescription as getNotifyMemberViaEmailCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/NotifyMemberViaEmailCard';
import { getDescription as getNotifyMemberViaTextCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/NotifyMemberViaTextCard';
import { getDescription as getAssignTeamMemberCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/AssignTeamMemberCard';
import { getDescription as getGoToAutomationStepCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/GoToAutomationStepCard';
import { getDescription as getUpdateVariableCardCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/UpdateVariableCard';
import { getDescription as getCodeStepCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/CodeStepCard';
import { getDescription as getCallLLMCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/CallLLMCard';
import { getDescription as getFileExtractionCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/FileExtractionCard';
import { getDescription as getAudioTranscriptionCardDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/AudioTranscriptionCard';
import { getDescription as getStopExecutionDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/StopExecutionCard';
import { getDescription as getExitLoopDescription } from 'pages/AutomationEngine/Engine/Steps/type/action/ExitLoopCard';
import { getDescription as getPluginCodeStepCard } from 'pages/AutomationEngine/Engine/Steps/type/action/PluginCodeStepCard';

import { descriptionChanged } from './utils';
import { useAutomationsSelector } from '../store/react';
import { getHistoryId, getMetaData } from '../store/selectors';
import type { AutomationMetaData } from '../types';
import type { AutomationStep } from 'ts-components/Plugins/types';
import { PluginContext } from 'ts-components/Plugins/context';

type DescriptionDictionaryType = Record<string, (...args: any[]) => any>;

const descriptionDictionary: DescriptionDictionaryType = {
  [AUTOMATION_STEPS.modify_related_entities.type]:
    getModifyRelatedEntitiesDescription,
  [AUTOMATION_STEPS.schedule_activity.type]: getScheduleActivityCardDescription,
  [AUTOMATION_STEPS.delete_scheduled_activity.type]:
    getDeleteScheduledActivityCardDescription,
  [AUTOMATION_STEPS.start_automation.type]: getStartAutomationCardDescription,
  [AUTOMATION_STEPS.modify_automation.type]:
    getModifyAutomationsCardDescription,
  [AUTOMATION_STEPS.archive_record.type]: getArchiveRecordCardDescription,
  [AUTOMATION_STEPS.search_records.type]: getSearchRecordsCardDescription,
  [AUTOMATION_STEPS.http_request.type]: getHttpRequestCardDescription,
  [AUTOMATION_STEPS.change_field_value.type]:
    getChangeFieldValueCardDescription,
  [AUTOMATION_STEPS.change_tags.type]: getChangeTagsCardDescription,
  [AUTOMATION_STEPS.math_operator.type]: getMathOperatorCardDescription,
  [AUTOMATION_STEPS.send_email.type]: getSendEmailCardDescription,
  [AUTOMATION_STEPS.send_text.type]: getSendTextCardDescription,
  [AUTOMATION_STEPS.send_related_contact_text.type]:
    getSendRelatedContactTextCardDescription,
  [AUTOMATION_STEPS.create_related_entity.type]:
    getCreateRelatedEntityCardDescription,
  [AUTOMATION_STEPS.send_related_contact_email.type]:
    getSendRelatedContactEmailCardDescription,
  [AUTOMATION_STEPS.notify_member_via_email.type]:
    getNotifyMemberViaEmailCardDescription,
  [AUTOMATION_STEPS.notify_member_via_text.type]:
    getNotifyMemberViaTextCardDescription,
  [AUTOMATION_STEPS.assign_team_member.type]:
    getAssignTeamMemberCardDescription,
  [AUTOMATION_STEPS.go_to_automation_step.type]:
    getGoToAutomationStepCardDescription,
  [AUTOMATION_STEPS.update_variable.type]: getUpdateVariableCardCardDescription,
  [AUTOMATION_STEPS.code_step.type]: getCodeStepCardDescription,
  [AUTOMATION_STEPS.call_llm.type]: getCallLLMCardDescription,
  [AUTOMATION_STEPS.file_content_extraction.type]:
    getFileExtractionCardDescription,
  [AUTOMATION_STEPS.audio_transcription.type]:
    getAudioTranscriptionCardDescription,
  [AUTOMATION_STEPS.stop_execution.type]: getStopExecutionDescription,
  [AUTOMATION_STEPS.exit_loop.type]: getExitLoopDescription,
  [AUTOMATION_STEPS.plugin_code_step.type]: getPluginCodeStepCard,
};

type TypeGetStepDescriptions = {
  step: any;
  automation: any;
  fields: any;
  business: any;
  groupedOptions: any;
  metaData: AutomationMetaData;
  automationSteps: AutomationStep[];
  historyMode?: boolean;
  providerModelLookup?: Record<string, string>;
  t: TFunction;
};

export const getActionDescriptions = ({
  step,
  automation,
  fields,
  business,
  groupedOptions,
  metaData,
  automationSteps,
  historyMode,
  providerModelLookup,
  t,
}: TypeGetStepDescriptions) => {
  const getDescFN = descriptionDictionary[step.type];

  const description = getDescFN
    ? getDescFN({
        step,
        automation,
        fields,
        business,
        groupedOptions,
        metaData,
        automationSteps,
        historyMode,
        providerModelLookup,
        t,
      })
    : step.description;

  return description || step.description;
};

export const useAddActionDescriptions = (
  actionDescriptionQue: string[],
  steps: any[],
  automation: any,
  business: any,
  fields: any,
  groupedOptions: any,
  providerModelLookup: Record<string, string>,
  dispatch: any
) => {
  const { t } = useTranslation();

  const metaData: AutomationMetaData = useAutomationsSelector(getMetaData);
  const historyId = useAutomationsSelector(getHistoryId);
  const { automationSteps } = useContext(PluginContext);

  const firstStepProcessed = useRef(false);

  const [que, setQue] = useState(actionDescriptionQue);
  const queIntialized = useRef(false);

  if (actionDescriptionQue.length && !queIntialized.current) {
    queIntialized.current = true;
    setQue(actionDescriptionQue);
  }

  const processAction = useCallback(
    (step: any) => {
      const description = getActionDescriptions({
        step,
        automation,
        fields,
        business,
        groupedOptions,
        metaData,
        automationSteps,
        historyMode: Boolean(historyId),
        providerModelLookup,
        t,
      });

      const queName = 'actionDescriptionQue';

      if (descriptionChanged(description, step)) {
        dispatch(
          actions.updateStep({
            step: { ...step, description },
            queName,
            dirty: false,
          })
        );
      }

      setQue((prevQue) => {
        const nextQue: string[] = prevQue.filter((id) => id !== step.id);

        if (nextQue.length === 0) {
          dispatch(
            actions.clearDescriptionQue({
              queName,
            })
          );
        }
        return nextQue;
      });
    },
    [
      automation,
      fields,
      business,
      groupedOptions,
      metaData,
      automationSteps,
      t,
      dispatch,
      historyId,
      providerModelLookup,
    ]
  );

  const stepToProcess =
    que.length > 0 && steps.length > 0 && business && fields.length > 0
      ? steps.find((s) => s.id === que[0])
      : undefined;

  useEffect(() => {
    let id: any;
    if (stepToProcess) {
      if (!firstStepProcessed.current) {
        id = setTimeout(() => {
          firstStepProcessed.current = true;
          processAction(stepToProcess);
        }, 1000);
      } else {
        processAction(stepToProcess);
      }
    }
    return () => clearTimeout(id);
  }, [stepToProcess, processAction]);
};
